/**
 * The seeded generator teaching-model samples are drawn from, the same on the server and in the
 * browser: xoshiro128** seeded from the integer seed by splitmix32.
 */

/** xoshiro128** from a 128-bit state: each call returns the next 32-bit output as an unsigned integer. */
export function xoshiro128(s0: number, s1: number, s2: number, s3: number): () => number {
	let a = s0 | 0;
	let b = s1 | 0;
	let c = s2 | 0;
	let d = s3 | 0;
	return () => {
		const result = Math.imul(rotl(Math.imul(b, 5), 7), 9) >>> 0;
		const t = b << 9;
		c ^= a;
		d ^= b;
		b ^= c;
		a ^= d;
		c ^= t;
		d = rotl(d, 11);
		return result;
	};
}

function rotl(x: number, k: number): number {
	return (x << k) | (x >>> (32 - k));
}

/** One splitmix32 step from `state`: a bijection of the state, so different states give different words. */
function splitmix32(state: number): number {
	let z = (state + 0x9e3779b9) | 0;
	z = Math.imul(z ^ (z >>> 16), 0x85ebca6b);
	z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35);
	return (z ^ (z >>> 16)) >>> 0;
}

/**
 * Uniform doubles in [0, 1) with 53 random bits, from a safe integer seed. The seed's low and high
 * 32 bits seed two halves of the state, so different seeds start from different states.
 */
export function seededUniform(seed: number): () => number {
	const low = seed >>> 0;
	const high = Math.floor(seed / 2 ** 32) >>> 0;
	const a = splitmix32(low);
	const b = splitmix32(a ^ 0x6a09e667);
	const c = splitmix32(high ^ 0xbb67ae85);
	const d = splitmix32(c ^ 0x3c6ef372);
	const next = xoshiro128(a, b, c, d);
	return () => ((next() >>> 5) * 2 ** 26 + (next() >>> 6)) / 2 ** 53;
}
